import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Footer from '../Components/Footer';
import Auth from '../Function/Auth';
import Header from '../Components/Header';
import { toast, ToastContainer } from 'react-toastify';

function MasterWallet() {
  const authToken = sessionStorage.getItem('authToken');
  const baseurl = process.env.REACT_APP_BASE_API_URL;

  const [tableLoading, setTableLoading] = useState(false);
  const [assetData, setAssetData] = useState([]);
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 10,
    total: 0,
  });

  const [assetName, setAssetName] = useState('');
  const [assetStatus, setAssetStatus] = useState('');

  // Modal states
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [showUpdateForm, setShowUpdateForm] = useState(false);
  const [updateAddress, setUpdateAddress] = useState('');
  const [updatePrivateKey, setUpdatePrivateKey] = useState('');

  useEffect(() => {
    fetchAssets(1);
  }, []);

  const fetchAssets = async (page) => {
    if (!authToken) return;

    setTableLoading(false);

    const payload = {
      page,
      name: assetName.trim(),
      status: assetStatus,
    };

    try {
      const response = await fetch(`${baseurl}/assets`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify(payload),
      });

      if (response.status === 429) {
        toast.error('Rate limit exceeded. Try again later.');
        return;
      }

      const result = await response.json();

      if (result.status) {
        setAssetData(result.data || []);
        setPagination(result.pagination || {
          current_page: 1,
          last_page: 1,
          per_page: 10,
          total: 0,
        });
      } else {
        toast.error(result.message || 'Failed to load assets');
      }
    } catch (err) {
      console.error('Fetch error:', err);
      toast.error('Network error occurred');
    } finally {
      setTableLoading(true);
    }
  };

  const handleSearch = () => {
    fetchAssets(1);
  };

  const handlePageChange = (page) => {
    if (page < 1 || page > pagination.last_page) return;
    fetchAssets(page);
  };

  const openViewModal = (asset) => {
    setSelectedAsset(asset);
    setShowUpdateForm(false);
    setUpdateAddress(asset?.withdraw_address || '');
    setUpdatePrivateKey(''); // private key usually not pre-filled
  };

  const handleUpdateWallet = async () => {
    if (!selectedAsset?.id) return toast.error('No asset selected');
    if (!updateAddress.trim()) return toast.error('Address is required');

    // You can decide whether private key is mandatory
    // if (!updatePrivateKey.trim()) return toast.error('Private key is required');

    const payload = {
      asset_id: selectedAsset.id,
      address: updateAddress.trim(),
      key: updatePrivateKey.trim() || null, // send null if empty
    };

    try {
      const response = await fetch(`${baseurl}/assets/update-wallet`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (result.status) {
        toast.success(result.message || 'Wallet updated successfully');
        setShowUpdateForm(false);
        fetchAssets(pagination.current_page); // refresh list
      } else {
        toast.error(result.message || 'Update failed');
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to update wallet');
    }
  };

  return (
    <>
      <Header>
        <Auth />
        <div className="main-content app-content">
          <div className="container-fluid">
            {/* Page Header */}
            <div className="d-md-flex d-block align-items-center justify-content-between my-4 page-header-breadcrumb">
              <h4 className="fw-medium mb-0">Master Wallet</h4>
            </div>

            {/* Filter Card */}
            <div className="card mb-4">
              <div className="card-body">
                <b>Filter</b>
                <div className="row g-3 mt-2">
                  <div className="col-xl-3 col-lg-6">
                    <label className="form-label">Asset Name / Ticker</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Enter asset name or ticker"
                      value={assetName}
                      onChange={(e) => setAssetName(e.target.value)}
                    />
                  </div>

                  <div className="col-xl-3 col-lg-6">
                    <label className="form-label">Status</label>
                    <select
                      className="form-control"
                      value={assetStatus}
                      onChange={(e) => setAssetStatus(e.target.value)}
                    >
                      <option value="">All</option>
                      <option value="active">Active</option>
                      <option value="pending">Pending</option>
                    </select>
                  </div>

                  <div className="col-xl-3 col-lg-6 d-flex align-items-end">
                    <button
                      onClick={handleSearch}
                      className="btn btn-primary w-100"
                    >
                      Search
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Assets Table */}
            <div className="card card-body">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h3 className="card-title mb-0 text-primary">Asset List</h3>
              </div>

              <div className="table-responsive">
                {tableLoading ? (
                  <table className="table table-striped table-bordered mb-0 text-nowrap">
                    <thead>
                      <tr>
                        <th>Logo</th>
                        <th>Name / Ticker</th>
                        <th>Contract Address</th>
                        <th>Network</th>
                        <th>Withdraw Address</th>
                        <th>Status</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {assetData.length > 0 ? (
                        assetData.map((asset) => (
                          <tr key={asset.id}>
                            <td>
                              {asset.asset_logo ? (
                                <img
                                  src={asset.asset_logo}
                                  alt={asset.asset_ticker}
                                  className="avatar avatar-md rounded-circle"
                                />
                              ) : (
                                'N/A'
                              )}
                            </td>
                            <td>
                              <strong>{asset.asset_ticker || '—'}</strong>
                              <br />
                              {asset.asset_name || '—'}
                            </td>
                            <td>{asset.contract_address || '—'}</td>
                            <td>{asset.network || '—'}</td>
                            <td>{asset.withdraw_address || '—'}</td>
                            <td>
                              {asset.status === 'active' ? (
                                <span className="badge bg-success">Active</span>
                              ) : (
                                <span className="badge bg-danger">
                                  {asset.status || 'Inactive'}
                                </span>
                              )}
                            </td>
                            <td>
                              <button
                                className="btn btn-sm btn-primary"
                                data-bs-toggle="modal"
                                data-bs-target="#assetViewModal"
                                onClick={() => openViewModal(asset)}
                              >
                                <i className="fa fa-eye"></i>
                              </button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={7} className="text-center py-4">
                            No assets found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                ) : (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Pagination */}
              {pagination.total > 0 && (
                <div className="d-flex justify-content-between align-items-center p-3">
                  <div>
                    Showing {pagination.current_page} to {Math.min(pagination.current_page * pagination.per_page, pagination.total)} of {pagination.total}
                  </div>

                  <nav>
                    <ul className="pagination mb-0">
                      <li className={`page-item ${pagination.current_page === 1 ? 'disabled' : ''}`}>
                        <button
                          className="page-link"
                          onClick={() => handlePageChange(pagination.current_page - 1)}
                          disabled={pagination.current_page === 1}
                        >
                          Previous
                        </button>
                      </li>

                      {Array.from({ length: pagination.last_page }, (_, i) => i + 1)
                        .filter(page => {
                          if (pagination.last_page <= 7) return true;
                          if (page === 1 || page === pagination.last_page) return true;
                          return Math.abs(page - pagination.current_page) <= 2;
                        })
                        .map((page) => (
                          <li
                            key={page}
                            className={`page-item ${pagination.current_page === page ? 'active' : ''}`}
                          >
                            <button
                              className="page-link"
                              onClick={() => handlePageChange(page)}
                            >
                              {page}
                            </button>
                          </li>
                        ))}

                      <li className={`page-item ${pagination.current_page === pagination.last_page ? 'disabled' : ''}`}>
                        <button
                          className="page-link"
                          onClick={() => handlePageChange(pagination.current_page + 1)}
                          disabled={pagination.current_page === pagination.last_page}
                        >
                          Next
                        </button>
                      </li>
                    </ul>
                  </nav>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* View Asset Modal */}
        <div className="modal fade" id="assetViewModal" tabIndex="-1" aria-labelledby="assetViewModalLabel" aria-hidden="true">
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" id="assetViewModalLabel">
                  Asset Details
                </h5>
                <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
              </div>

              <div className="modal-body">
                {selectedAsset && (
                  <>
                    <div className="row mb-3">
                      <div className="col-5 fw-bold">Ticker / Name</div>
                      <div className="col-7">
                        {selectedAsset.asset_ticker} - {selectedAsset.asset_name}
                      </div>
                    </div>

                    <div className="row mb-3">
                      <div className="col-5 fw-bold">Contract Address</div>
                      <div className="col-7">{selectedAsset.contract_address || '—'}</div>
                    </div>

                    <div className="row mb-3">
                      <div className="col-5 fw-bold">Network</div>
                      <div className="col-7">{selectedAsset.network || '—'}</div>
                    </div>

                    <div className="row mb-3">
                      <div className="col-5 fw-bold">Withdraw Address</div>
                      <div className="col-7">{selectedAsset.withdraw_address || '—'}</div>
                    </div>

                    <div className="row mb-3">
                      <div className="col-5 fw-bold">Status</div>
                      <div className="col-7">
                        {selectedAsset.status === 'active' ? (
                          <span className="badge bg-success">Active</span>
                        ) : (
                          <span className="badge bg-danger">{selectedAsset.status}</span>
                        )}
                      </div>
                    </div>

                    {showUpdateForm && (
                      <>
                        <hr />
                        <h6 className="mb-3 text-primary">Update Wallet</h6>

                        <div className="mb-3">
                          <label className="form-label fw-bold">New Withdraw Address</label>
                          <input
                            type="text"
                            className="form-control"
                            value={updateAddress}
                            onChange={(e) => setUpdateAddress(e.target.value)}
                            placeholder="Enter new wallet address"
                          />
                        </div>

                        <div className="mb-3">
                          <label className="form-label fw-bold">Private Key (optional)</label>
                          <input
                            type="text"
                            className="form-control"
                            value={updatePrivateKey}
                            onChange={(e) => setUpdatePrivateKey(e.target.value)}
                            placeholder="Enter private key (sensitive)"
                          />
                          <small className="text-danger">Be careful — private key is sensitive information</small>
                        </div>
                      </>
                    )}
                  </>
                )}
              </div>

              <div className="modal-footer">
                {!showUpdateForm ? (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => setShowUpdateForm(true)}
                  >
                    Update Wallet
                  </button>
                ) : (
                  <>
                    <button
                      type="button"
                      className="btn btn-success"
                      onClick={handleUpdateWallet}
                    >
                      Save Changes
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => setShowUpdateForm(false)}
                    >
                      Cancel
                    </button>
                  </>
                )}
                <button type="button" className="btn btn-light" data-bs-dismiss="modal">
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>

        <ToastContainer position="top-right" autoClose={4000} />
      </Header>

      <Footer />
    </>
  );
}

export default MasterWallet;